<template>
  <div class="modulos-gestion">
    <div class="pagina-cabecera">
      <div>
        <h1 class="pagina-titulo">Módulos por Empresa</h1>
        <p class="pagina-subtitulo">Busca una empresa para ver sus módulos activos según el plan asignado.</p>
      </div>
    </div>

    <!-- Buscador de empresa -->
    <div class="buscador-empresa">
      <div class="campo-busqueda-wrap">
        <i class="bi bi-search campo-busqueda-icono"></i>
        <input
          v-model="busqueda"
          type="text"
          class="campo-busqueda"
          placeholder="Buscar empresa por nombre o RUC..."
          @input="buscarEmpresas"
        />
        <button v-if="busqueda" class="campo-busqueda-limpiar" @click="limpiarBusqueda">
          <i class="bi bi-x"></i>
        </button>
      </div>
      <div v-if="resultadosBusqueda.length" class="resultados-dropdown">
        <div
          v-for="emp in resultadosBusqueda"
          :key="emp.id || emp.ruc"
          class="resultado-item"
          @click="seleccionarEmpresa(emp)"
        >
          <div class="resultado-item__nombre">{{ emp.razon_social }}</div>
          <div class="resultado-item__ruc">{{ emp.ruc }}</div>
        </div>
      </div>
    </div>

    <!-- Empresa seleccionada -->
    <div v-if="empresaActiva" class="empresa-activa">
      <div class="empresa-activa__info">
        <div class="empresa-activa__avatar" :style="{ background: colorAvatar(empresaActiva.razon_social) }">
          {{ iniciales(empresaActiva.razon_social) }}
        </div>
        <div>
          <div class="empresa-activa__nombre">{{ empresaActiva.razon_social }}</div>
          <div class="empresa-activa__meta">
            {{ empresaActiva.ruc }} —
            Plan: <strong>{{ empresaActiva.plan_nombre || 'Sin plan' }}</strong>
          </div>
        </div>
      </div>
      <div class="empresa-activa__botones">
        <button class="btn btn--xs btn--primario" @click="abrirModalPlan">
          <i class="bi bi-arrow-repeat"></i> Cambiar plan
        </button>
        <button class="btn btn--xs btn--secundario" @click="limpiarEmpresa">
          <i class="bi bi-x"></i> Cambiar empresa
        </button>
      </div>
    </div>

    <div v-if="error" class="alerta alerta-error">
      <i class="bi bi-exclamation-triangle-fill"></i> {{ error }}
    </div>

    <div v-if="cargandoModulos" class="texto-vacio">
      <i class="bi bi-arrow-clockwise spin"></i> Cargando módulos...
    </div>

    <div v-else-if="!empresaActiva" class="texto-vacio texto-vacio--grande">
      <i class="bi bi-search"></i>
      <p>Busca y selecciona una empresa para ver sus módulos.</p>
    </div>

    <div v-else-if="!tienePlan" class="texto-vacio texto-vacio--grande">
      <i class="bi bi-exclamation-circle"></i>
      <p>Esta empresa no tiene un plan asignado.</p>
      <button class="btn btn--primario" @click="abrirModalPlan">
        <i class="bi bi-plus-circle"></i> Asignar plan
      </button>
    </div>

    <!-- Módulos agrupados (solo lectura) -->
    <div v-else-if="modulos.length" class="modulos-wrap">
      <div class="leyenda-fila">
        <span class="leyenda-item leyenda-item--activo"><i class="bi bi-check-circle-fill"></i> Incluido en el plan</span>
        <span class="leyenda-item leyenda-item--inactivo"><i class="bi bi-circle"></i> No incluido</span>
      </div>
      <div v-for="principal in modulos" :key="principal.id" class="modulo-seccion">
        <div class="modulo-seccion__cab">
          <span class="modulo-seccion__nombre">
            <i :class="iconoModulo(principal.ruta_modulo)"></i>
            {{ principal.nombre_modulo }}
          </span>
          <span class="modulo-seccion__conteo">
            {{ contarHabilitados(principal) }} / {{ principal.secundarios.length }}
          </span>
        </div>
        <div class="modulo-seccion__items">
          <div
            v-for="sec in principal.secundarios"
            :key="sec.id"
            class="modulo-item"
            :class="{ 'modulo-item--habilitado': sec.habilitado }"
          >
            <i :class="sec.habilitado ? 'bi bi-check-circle-fill modulo-icono--activo' : 'bi bi-circle modulo-icono--inactivo'"></i>
            <span class="modulo-item__nombre">{{ sec.nombre }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="empresaActiva" class="texto-vacio">
      Sin módulos configurados para esta empresa.
    </div>

    <!-- Modal: Cambiar/Asignar plan -->
    <div v-if="modalPlan.visible" class="modal-overlay" @click.self="cerrarModalPlan">
      <div class="modal-contenedor">
        <div class="modal-cabecera">
          <div>
            <h3>Asignar Plan</h3>
            <p class="modal-subtitulo">{{ empresaActiva?.razon_social }}</p>
          </div>
          <button class="btn-icono" @click="cerrarModalPlan"><i class="bi bi-x-lg"></i></button>
        </div>
        <div v-if="modalPlan.cargando" class="modal-cuerpo modal-cuerpo--centrado">
          <i class="bi bi-arrow-clockwise spin grande"></i>
          <span>Cargando planes...</span>
        </div>
        <div v-else class="modal-cuerpo">
          <div v-if="!planesDisponibles.length" class="texto-vacio">No hay planes activos disponibles.</div>
          <label
            v-for="plan in planesDisponibles"
            :key="plan.id"
            class="plan-opcion"
            :class="{ 'plan-opcion--seleccionado': modalPlan.planIdSeleccionado === plan.id }"
          >
            <input type="radio" :value="plan.id" v-model="modalPlan.planIdSeleccionado" />
            <div class="plan-opcion__info">
              <span class="plan-opcion__nombre">{{ plan.nombre }}</span>
              <span class="plan-opcion__detalle">
                ${{ formatear(plan.costo) }}/mes · {{ plan.total_modulos }} módulos
                <span v-if="plan.acceso_movil"> · <i class="bi bi-phone"></i> Móvil</span>
              </span>
            </div>
          </label>
        </div>
        <div class="modal-pie">
          <button type="button" class="btn btn--secundario" @click="cerrarModalPlan">Cancelar</button>
          <button
            type="button"
            class="btn btn--primario"
            :disabled="!modalPlan.planIdSeleccionado || modalPlan.guardando"
            @click="asignarPlan"
          >
            <span v-if="modalPlan.guardando"><i class="bi bi-arrow-clockwise spin"></i> Guardando...</span>
            <span v-else>Asignar plan</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

const ICONOS_MODULO = {
  dashboard: 'bi bi-house-door', ventas: 'bi bi-receipt', pedidos: 'bi bi-bag-check',
  proformas: 'bi bi-file-earmark-text', clientes: 'bi bi-people', inventario: 'bi bi-box-seam',
  compras: 'bi bi-bag', caja: 'bi bi-cash-stack', reportes: 'bi bi-bar-chart',
  configuracion: 'bi bi-gear', contabilidad: 'bi bi-calculator',
}

let timerBusqueda = null

export default {
  name: 'ModulosGestion',
  data() {
    return {
      busqueda: '', resultadosBusqueda: [],
      empresaActiva: null,
      modulos: [],
      cargandoModulos: false,
      error: null,
      planesDisponibles: [],
      modalPlan: {
        visible: false, cargando: false, guardando: false, planIdSeleccionado: null,
      },
    }
  },
  computed: {
    tienePlan() {
      return this.modulos.some(p => p.secundarios.some(s => s.habilitado))
        || (this.empresaActiva && this.empresaActiva.plan_nombre)
    },
  },
  methods: {
    buscarEmpresas() {
      clearTimeout(timerBusqueda)
      if (this.busqueda.length < 2) { this.resultadosBusqueda = []; return }
      timerBusqueda = setTimeout(async () => {
        try {
          const res = await api.get('/admin/empresas', { params: { q: this.busqueda, limit: 8 } })
          this.resultadosBusqueda = res.data?.datos || []
        } catch (e) { /* búsqueda silenciosa */ }
      }, 300)
    },
    async seleccionarEmpresa(emp) {
      this.empresaActiva     = emp
      this.resultadosBusqueda = []
      this.busqueda          = ''
      this.error             = null
      this.cargandoModulos   = true
      this.modulos           = []
      try {
        const res = await api.get(`/admin/empresas/${emp.id}/modulos-plan`)
        this.modulos = res.data?.datos || []
      } catch (e) {
        this.error = 'Error al cargar módulos: ' + (e.response?.data?.mensaje || e.message)
      } finally { this.cargandoModulos = false }
    },
    async abrirModalPlan() {
      this.modalPlan.visible = true
      this.modalPlan.planIdSeleccionado = null
      if (this.planesDisponibles.length) return
      this.modalPlan.cargando = true
      try {
        const res = await api.get('/admin/planes')
        this.planesDisponibles = (res.data?.datos || []).filter(p => p.activo)
      } catch (e) {
        this.error = 'Error al cargar planes.'
      } finally { this.modalPlan.cargando = false }
    },
    cerrarModalPlan() { this.modalPlan.visible = false },
    async asignarPlan() {
      if (!this.modalPlan.planIdSeleccionado) return
      this.modalPlan.guardando = true
      try {
        await api.post(`/admin/empresas/${this.empresaActiva.id}/asignar-plan`, {
          plan_id: this.modalPlan.planIdSeleccionado,
        })
        const planAsignado = this.planesDisponibles.find(p => p.id === this.modalPlan.planIdSeleccionado)
        if (planAsignado) this.empresaActiva.plan_nombre = planAsignado.nombre
        await this.seleccionarEmpresa(this.empresaActiva)
        this.cerrarModalPlan()
      } catch (e) {
        alert('Error al asignar el plan: ' + (e.response?.data?.mensaje || e.message))
      } finally { this.modalPlan.guardando = false }
    },
    limpiarBusqueda() { this.busqueda = ''; this.resultadosBusqueda = [] },
    limpiarEmpresa()  { this.empresaActiva = null; this.modulos = []; this.error = null },
    contarHabilitados(principal) {
      return principal.secundarios.filter(s => s.habilitado).length
    },
    iconoModulo(codigo) { return ICONOS_MODULO[codigo] || 'bi bi-puzzle' },
    iniciales(nombre) {
      return (nombre || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    },
    colorAvatar(nombre) {
      const colores = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899']
      let h = 0
      for (const c of (nombre || '')) h = ((h << 5) - h) + c.charCodeAt(0)
      return colores[Math.abs(h) % colores.length]
    },
    formatear(valor) { return parseFloat(valor || 0).toFixed(2) },
  },
}
</script>

<style scoped>
.modulos-gestion { padding:1.5rem; max-width:1200px; margin:0 auto; display:flex; flex-direction:column; gap:1.25rem; }
.pagina-cabecera { display:flex; align-items:flex-start; justify-content:space-between; }
.pagina-titulo   { font-size:1.5rem; font-weight:700; color:var(--text-primary); margin:0 0 .25rem; }
.pagina-subtitulo{ font-size:.875rem; color:var(--text-secondary); margin:0; }
.alerta          { padding:.75rem 1rem; border-radius:.5rem; display:flex; gap:.5rem; }
.alerta-error    { background:color-mix(in srgb,var(--accent-danger) 10%,transparent); color:var(--accent-danger); border:1px solid color-mix(in srgb,var(--accent-danger) 30%,transparent); }

/* Buscador */
.buscador-empresa      { position:relative; max-width:480px; }
.campo-busqueda-wrap   { display:flex; align-items:center; border:1px solid var(--border-main); border-radius:.5rem; background:var(--bg-main); padding:0 .75rem; transition:border-color .15s; }
.campo-busqueda-wrap:focus-within { border-color:var(--border-focus); }
.campo-busqueda-icono  { color:var(--text-muted); flex-shrink:0; }
.campo-busqueda        { flex:1; background:none; border:none; padding:.6rem .5rem; color:var(--text-primary); font-size:.875rem; outline:none; }
.campo-busqueda-limpiar{ background:none; border:none; cursor:pointer; color:var(--text-muted); padding:.2rem; display:flex; align-items:center; }
.resultados-dropdown   { position:absolute; top:calc(100% + .25rem); left:0; right:0; background:var(--secondary-bg-main); border:1px solid var(--border-main); border-radius:.5rem; box-shadow:var(--shadow-md); z-index:100; overflow:hidden; }
.resultado-item        { padding:.6rem .75rem; cursor:pointer; transition:background .1s; }
.resultado-item:hover  { background:var(--bg-main); }
.resultado-item__nombre{ font-size:.875rem; font-weight:600; color:var(--text-primary); }
.resultado-item__ruc   { font-size:.75rem; color:var(--text-muted); }

/* Empresa activa */
.empresa-activa        { display:flex; align-items:center; justify-content:space-between; background:var(--secondary-bg-main); border:1px solid var(--border-main); border-radius:.75rem; padding:1rem 1.25rem; gap:1rem; flex-wrap:wrap; }
.empresa-activa__info  { display:flex; align-items:center; gap:1rem; }
.empresa-activa__avatar{ width:2.5rem; height:2.5rem; border-radius:.5rem; display:flex; align-items:center; justify-content:center; font-size:.75rem; font-weight:700; color:#fff; flex-shrink:0; }
.empresa-activa__nombre{ font-size:1rem; font-weight:700; color:var(--text-primary); }
.empresa-activa__meta  { font-size:.8rem; color:var(--text-secondary); }
.empresa-activa__botones { display:flex; gap:.5rem; }

/* Leyenda */
.leyenda-fila  { display:flex; gap:1rem; flex-wrap:wrap; }
.leyenda-item  { display:flex; align-items:center; gap:.35rem; font-size:.78rem; color:var(--text-secondary); }
.leyenda-item--activo i   { color:var(--accent-success); }
.leyenda-item--inactivo i { color:var(--text-muted); }

/* Módulos */
.modulos-wrap   { display:flex; flex-direction:column; gap:1rem; }
.modulo-seccion { border:1px solid var(--border-main); border-radius:.5rem; overflow:hidden; }
.modulo-seccion__cab { display:flex; align-items:center; justify-content:space-between; padding:.65rem 1rem; background:var(--bg-main); }
.modulo-seccion__nombre { display:flex; align-items:center; gap:.5rem; font-size:.875rem; font-weight:700; color:var(--text-primary); }
.modulo-seccion__conteo { font-size:.75rem; color:var(--text-muted); }
.modulo-seccion__items  { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); }
.modulo-item     { display:flex; align-items:center; gap:.5rem; padding:.55rem 1rem; border-top:1px solid var(--border-main); font-size:.825rem; }
.modulo-item__nombre { flex:1; color:var(--text-secondary); }
.modulo-item--habilitado .modulo-item__nombre { color:var(--text-primary); font-weight:500; }
.modulo-icono--activo  { color:var(--accent-success); flex-shrink:0; }
.modulo-icono--inactivo{ color:var(--border-main); flex-shrink:0; }

/* Modal planes */
.btn-icono       { background:none; border:1px solid var(--border-main); border-radius:.375rem; padding:.3rem .5rem; cursor:pointer; color:var(--text-secondary); transition:all .15s; }
.btn-icono:hover { background:var(--bg-main); color:var(--text-primary); }
.modal-overlay   { position:fixed; inset:0; background:rgba(0,0,0,.5); display:flex; align-items:center; justify-content:center; z-index:1000; padding:1rem; }
.modal-contenedor{ background:var(--secondary-bg-main); border-radius:.75rem; width:100%; max-width:520px; max-height:85vh; display:flex; flex-direction:column; box-shadow:var(--shadow-md); }
.modal-cabecera  { padding:1.25rem 1.5rem; border-bottom:1px solid var(--border-main); display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; }
.modal-cabecera h3 { margin:0; font-size:1.1rem; font-weight:700; color:var(--text-primary); }
.modal-subtitulo { font-size:.8rem; color:var(--text-secondary); margin:.25rem 0 0; }
.modal-cuerpo    { padding:1.5rem; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:.75rem; }
.modal-cuerpo--centrado { align-items:center; justify-content:center; color:var(--text-secondary); }
.modal-pie       { padding:1rem 1.5rem; border-top:1px solid var(--border-main); display:flex; justify-content:flex-end; gap:.75rem; }
.plan-opcion     { display:flex; align-items:center; gap:1rem; padding:.75rem 1rem; border:1px solid var(--border-main); border-radius:.5rem; cursor:pointer; transition:all .15s; }
.plan-opcion:hover { background:var(--bg-main); }
.plan-opcion--seleccionado { border-color:var(--primary-main); background:color-mix(in srgb,var(--primary-main) 6%,transparent); }
.plan-opcion input { flex-shrink:0; cursor:pointer; accent-color:var(--primary-main); }
.plan-opcion__info { display:flex; flex-direction:column; gap:.2rem; }
.plan-opcion__nombre { font-size:.9rem; font-weight:700; color:var(--text-primary); }
.plan-opcion__detalle { font-size:.78rem; color:var(--text-secondary); }

/* Botones */
.btn           { display:inline-flex; align-items:center; gap:.4rem; border:none; border-radius:.5rem; padding:.6rem 1.2rem; cursor:pointer; font-size:.875rem; font-weight:600; transition:all .15s; }
.btn:disabled  { opacity:.5; cursor:not-allowed; }
.btn--primario { background:var(--primary-main); color:#fff; }
.btn--primario:hover { background:var(--primary-hover); }
.btn--secundario { background:var(--bg-main); color:var(--text-primary); border:1px solid var(--border-main); }
.btn--secundario:hover { background:var(--border-main); }
.btn--xs { padding:.3rem .75rem; font-size:.8rem; }

/* Varios */
.texto-vacio         { text-align:center; color:var(--text-muted); padding:2rem; font-size:.875rem; }
.texto-vacio--grande { padding:4rem 2rem; font-size:3rem; display:flex; flex-direction:column; align-items:center; gap:1rem; }
.texto-vacio--grande p { font-size:.875rem; color:var(--text-secondary); }
.spin { animation:rotar 1s linear infinite; display:inline-block; }
@keyframes rotar { to { transform:rotate(360deg); } }
.grande { font-size:2rem; }
</style>
